import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TFA from '../../../components/TFA';
import UserContext from '../../../UserContext/UserContext';
import {
  setcustomJWT,
  checkUserAccount,
  setUserDevice,
  signUpUser,
  getOTP,
  getUserProfile,
} from '../../../api';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectPhoneNumber,
  selectVerificationId,
  selectFirebaseJWT,
  setFirebaseJWT,
  setJwtToken,
  selectJwtToken,
  selectAccount,
  setAccount,
  setAvatar,
} from '../../../redux/features/user/userSlice';
import { toast } from 'react-toastify';
import { auth } from '../../../firebase';
import useUserRole from '../../../utils/useUserRole';
import { initializeSocketConnection } from '../../../redux/socketEvents';

const TwoFactorAuth = () => {
  const { resendOTP, checkUserDevice, logout } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const userRole = useUserRole();
  const { state } = useLocation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { phone, verificationId, firebaseJWT, jwtToken, inviteRFCode } = useSelector((state) => ({
    phone: selectPhoneNumber(state),
    verificationId: selectVerificationId(state),
    firebaseJWT: selectFirebaseJWT(state),
    jwtToken: selectJwtToken(state),
    inviteRFCode: selectAccount(state)?.inviteRFCode,
  }));

  // when user is at otp route
  const redirectOtp = () => {
    if (location.pathname === '/two-factor-auth') {
      //if user deivice is trusted
      if (jwtToken) return navigate('/user/dashboard');
    }

    // when user tries to navigate via url
    if (location.pathname === '/two-factor-auth') {
      //when trust device /when user is authenticated go to dashboard
      if (firebaseJWT && jwtToken) return navigate('/user/dashboard');
    }
  };

  const setUserData = async (token, data) => {
    try {
      const response = await getUserProfile(token);
      
      // Check if response exists and has valid status
      if (!response) {
        throw new Error('No response received from getUserProfile');
      }
      
      // Check if response has status property (if it's an HTTP response)
      if (response.status && response.status !== 200) {
        throw new Error(`getUserProfile failed with status: ${response.status}`);
      }
      
      // Extract data from response.data
      const userProfileData = response.data || response;
      
      // Validate that response has the expected structure
      if (!userProfileData.account) {
        throw new Error('Invalid response structure: missing account data');
      }
      
      const { account, ...newObj } = userProfileData;
      dispatch(setAccount({ ...newObj, ...userProfileData?.account, ...{ accInfo: data } }));
      dispatch(setAvatar(userProfileData?.account?.avatar));
    } catch (error) {
      console.error('setUserData error:', error);
      throw error; // Re-throw to be handled by calling function
    }
  };
  
  const userAccountCheck = async (token) => {
    try {
      const response = await checkUserAccount(token);
      
      // Check if response exists and has valid structure
      if (!response) {
        throw new Error('No response received from checkUserAccount');
      }
      
      // Check if response has status property (if it's an HTTP response)
      if (response.status && response.status !== 200) {
        throw new Error(`checkUserAccount failed with status: ${response.status}`);
      }
      
      // Extract userData from response.data
      const userData = response.data || response;
      
      // Validate that userData has the expected structure based on API response
      const expectedProperties = ['role', 'account', 'personalInfo', 'location', 'documents', 'verificationState', 'canTransact'];
      const missingProperties = expectedProperties.filter(prop => !(prop in userData));
      
      // if (missingProperties.length > 0) {
      //   throw new Error(`Invalid response structure: missing properties: ${missingProperties.join(', ')}`);
      // }
      
      // Validate that account property is a boolean
      if (typeof userData.account !== 'boolean') {
        throw new Error('Invalid response structure: account property must be a boolean');
      }

      if (!userData.account) {
        if (state?.signup) {
          console.log(inviteRFCode);
          const signUpResponse = await signUpUser(token, inviteRFCode);

          // Check signUpResponse status and data
          if (!signUpResponse) {
            throw new Error('No response received from signUpUser');
          }
          
          if (signUpResponse.status === 200) {
            toast.success('Signup successful');
            await setUserData(token, userData);
            // navigate('/auth/trust-device', { state: { reg: true, linkBack: state?.linkBack } });
          } else {
            throw new Error(signUpResponse.message || `Signup failed with status: ${signUpResponse.status}`);
          }
        } else {
          // Extract only serializable data from userData
          const serializableUserData = {
            phone: userData.phone,
            role: userData.role,
            account: userData.account,
            personalInfo: userData.personalInfo,
            location: userData.location,
            documents: userData.documents,
            verificationState: userData.verificationState,
            canTransact: userData.canTransact
          };
          navigate('/auth/user-not-found', { state: { userData: serializableUserData } });
        }
      } else {
        await setUserData(token, userData);
        await checkUserDevice(token, userData?.role);
      }
    } catch (error) {
      console.error('userAccountCheck error:', error);
      setLoading(false);
      toast.error(`Something went wrong...${error?.message || 'Oops!'}`);
      logout();
    }
  };

  const handleOtp = async (otp) => {
    try {
      setLoading(true);
      //if verificationID use firebase
      if (verificationId) {
        const confirmationResult = verificationId;
        //   const credential = firebase.auth.PhoneAuthProvider.credential(verificationId, otp);
        //   // Verify user
        //   await firebase.auth().signInWithCredential(credential);
        await confirmationResult.confirm(otp);
        // Get firebase jwt
        const idToken = await auth.currentUser.getIdToken(/* forceRefresh */ true);
        // Save token
        dispatch(setFirebaseJWT(idToken));

       
        // Check if user has account
        await userAccountCheck(idToken);
        dispatch(initializeSocketConnection());
      } else {
        const { customToken } = (await setcustomJWT(phone, otp)).data;
        if (customToken) {
          await auth().signInWithCustomToken(customToken);
          const jwtFirebase = await auth.currentUser.getIdToken(/* forceRefresh */ true);
          // Save token
          dispatch(setFirebaseJWT(jwtFirebase));

          //check if user has account for custom auth
          await userAccountCheck(jwtFirebase);
          dispatch(initializeSocketConnection());
        }
      }
      setLoading(false);
      // navigate to
    } catch (error) {
      setError(true);
      console.log('handleOtp', error);
      toast.error(`Something went wrong...${error?.message || 'Oops!'}`);
      setLoading(false);
    }
  };

  const handleOtpDevice = async (otp) => {
    try {
      setLoading(true);
      const data = await setUserDevice(otp, firebaseJWT);

      // Check if data exists and has valid structure
      if (!data) {
        throw new Error('No response received from setUserDevice');
      }
      
      // Check if data has status property (if it's an HTTP response)
      if (data.status && data.status !== 200) {
        throw new Error(`setUserDevice failed with status: ${data.status}`);
      }
      
      // Validate that data has the expected structure
      if (!data.access_token) {
        throw new Error('Invalid response structure: missing access_token');
      }

      if (data.access_token) {
        dispatch(setJwtToken(data.access_token));

        if (!data.account && !state.flow && state.reg) {
          return navigate('/auth/thanks/registration', { state: { registration: true } });
        } else if (state.buyFlow || state.flow || state?.linkBack) {
          // Extract only serializable properties from state
          const serializableState = {
            buyFlow: state.buyFlow,
            flow: state.flow,
            linkBack: state.linkBack,
            reg: state.reg,
            // Add other necessary serializable properties
          };
          return navigate(state?.linkBack || `/${userRole}/dashboard`, { state: serializableState });
        } else {
          return navigate(`/${userRole}/dashboard`);
        }
      } else {
        throw new Error(data.message || 'Access token not available');
      }
    } catch (error) {
      console.error('Error in handleOtpDevice:', error);
      setError(true);

      if (error.message === 'Access token not available') {
        toast.error('Failed to authenticate. Please try again.');
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }

      setLoading(false);
    }
  };

  const handleResendOTPDevice = async () => {
    try {
      const response = await getOTP(firebaseJWT);
      
      // Check if response exists
      if (!response) {
        throw new Error('No response received from getOTP');
      }
      
      // Check if response has status property (if it's an HTTP response)
      if (response.status && response.status !== 200) {
        throw new Error(`getOTP failed with status: ${response.status}`);
      }
      
      // Optional: Check if response has success indicator
      if (response.success === false) {
        throw new Error(response.message || 'Failed to resend OTP');
      }
      
    } catch (error) {
      console.log('handleResendOTPDevice', error);
      toast.error('Failed to resend');
      setLoading(false);
    }
  };

  const handleResendOTP = () => resendOTP(phone);

  // redirect user when navigating
  useEffect(() => {
    redirectOtp();
  }, [location]);

  return (
    <TFA
      number={phone}
      handle={state?.device ? handleOtpDevice : handleOtp}
      resendFn={state?.device ? handleResendOTPDevice : handleResendOTP}
      trust={state?.device ? true : false}
      loading={loading}
      setError={setError}
      error={error}
      isAuthFlow={true}
    />
  );
};

export default TwoFactorAuth;
